import type React from "react";
import { createContext, useContext, useMemo, useRef } from "react";
import type { BoundTurnstileObject } from "react-turnstile";
import { isWeb } from "@medbillai/ui";
import { Flag } from "@medbillai/utils/flag";
import { analyticsService } from "../../../lib/analytics/analytics";
import { useBoolVariation } from "../../../lib/flag";
const isBotControlOverrideEnabled = process.env.NEXT_PUBLIC_BOT_CONTROL_OVERRIDE_ENABLED === "true";
export type BotControlProvider = {
  getBotControlToken: () => string | undefined;
  resetBotControl: () => void;
  onBotControlVerified: (token: string, component: BoundTurnstileObject) => void;
  onBotControlExpired: () => void;
  isBotControlEnabled: boolean;
};
const initialContext: BotControlProvider = {
  getBotControlToken: () => undefined,
  resetBotControl: () => {
    throw new Error("Context not initialized");
  },
  onBotControlVerified: () => {
    throw new Error("Context not initialized");
  },
  onBotControlExpired: () => {
    throw new Error("Context not initialized");
  },
  isBotControlEnabled: false
};
export const BotControlContext = createContext<BotControlProvider>(initialContext);
type BotControlProviderProps = {
  children: React.ReactNode;
};
export const BotControlProvider = ({
  children
}: BotControlProviderProps) => {
  const botControlTokenRef = useRef<string | undefined>(undefined);
  const botControlComponentRef = useRef<BoundTurnstileObject | null>(null);

  // Bot control enabled by feature flag but can be forcefully overridden for local development.
  const isBotControlFeatureEnabled = useBoolVariation(Flag.BOT_CONTROL_ENABLED, false);
  const isBotControlEnabled = isWeb && (isBotControlFeatureEnabled || isBotControlOverrideEnabled);
  const contextValue = useMemo<BotControlProvider>(() => ({
    getBotControlToken: () => botControlTokenRef.current,
    resetBotControl: () => {
      botControlTokenRef.current = undefined;
      botControlComponentRef.current?.reset();
    },
    onBotControlVerified: (token: string, component: BoundTurnstileObject) => {
      analyticsService.trackEvent("BotControlChallengeSolved", {});
      botControlTokenRef.current = token;
      botControlComponentRef.current = component;
    },
    onBotControlExpired: () => {
      analyticsService.trackEvent("BotControlChallengeExpired", {});
      botControlTokenRef.current = undefined;
      botControlComponentRef.current?.reset();
    },
    isBotControlEnabled
  }), [isBotControlEnabled]);
  return <BotControlContext.Provider value={contextValue} data-sentry-element="BotControlContext.Provider" data-sentry-component="BotControlProvider" data-sentry-source-file="BotControlProvider.tsx">
      {children}
    </BotControlContext.Provider>;
};
export const useBotControl = (): BotControlProvider => {
  const context = useContext(BotControlContext);
  if (!context) {
    throw new Error("useBotControl must be used within a BotControlProvider");
  }
  return context;
};